// src/hooks/useAppleSignIn.ts
//
// Thin wrapper around Sign in with Apple JS — Apple's web SDK loaded by the
// <script src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js" />
// tag in index.html.
//
// Mirrors the shape of useGoogleSignIn so the auth pages can wire both buttons
// with the same ergonomics.
//
// Apple gotchas worth knowing:
//   - The user's name is sent ONLY on the very first sign-in for a given
//     (Services ID, Apple ID) pair, ONLY if the user agreed to share it, and
//     ONLY in the popup payload — NEVER in the id_token, NEVER again on later
//     sign-ins. We forward whatever Apple gives us to the backend; if it's
//     missing, the backend's new-user branch will collect it via the OAuth
//     profile step like any other field.
//   - The redirectURI must match a return URL registered on the Services ID
//     in the Apple Developer Portal, even though `usePopup: true` does not
//     navigate to it (Apple posts back via window.opener.postMessage). Apple
//     requires HTTPS — localhost dev needs a tunnel (ngrok / cloudflared)
//     or self-signed cert. Configured via VITE_APPLE_REDIRECT_URI.
//   - Apple's email may be an `@privaterelay.appleid.com` relay address. The
//     backend accepts these (per PLAN_SIGNUP_REDESIGN.md §6 relay-policy
//     decision); the frontend has nothing to do here.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const APPLE_CLIENT_ID = (import.meta.env.VITE_APPLE_CLIENT_ID ?? '').trim();
const APPLE_REDIRECT_URI = (import.meta.env.VITE_APPLE_REDIRECT_URI ?? '').trim();

export type AppleSignInError =
  | { reason: 'cancelled' }       // user closed the popup
  | { reason: 'unavailable' }     // SDK didn't load (network / blockers)
  | { reason: 'not_configured' }  // env vars missing — caller should keep button disabled
  | { reason: 'unknown'; detail?: string };

export interface AppleSignInResult {
  /** id_token issued by Apple. Verified server-side against Apple's JWKS. */
  idToken: string;
  /**
   * Optional name payload Apple includes on the first sign-in only. Forward
   * to the backend so it can pre-fill the OAuth profile form for new users.
   */
  name?: { firstName?: string; lastName?: string };
}

interface PendingResolver {
  resolve: (result: AppleSignInResult) => void;
  reject: (err: AppleSignInError) => void;
}

export function useAppleSignIn() {
  // We require both the client id (Services ID) AND a redirect URI — Apple's
  // init() rejects when redirectURI is missing even in popup mode.
  const configured = APPLE_CLIENT_ID.length > 0 && APPLE_REDIRECT_URI.length > 0;

  const [ready, setReady] = useState(false);
  const pendingRef = useRef<PendingResolver | null>(null);

  // Poll for window.AppleID. The <script> tag in index.html is async/defer
  // so we can't assume it's there at mount.
  useEffect(() => {
    if (!configured) return;

    let cancelled = false;
    let timer: number | undefined;

    const tryInit = () => {
      if (cancelled) return;
      const apple = (window as unknown as { AppleID?: AppleIDGlobal }).AppleID;
      if (!apple?.auth) {
        // Try again shortly. Same ~5s ceiling rationale as useGoogleSignIn.
        timer = window.setTimeout(tryInit, 200);
        return;
      }

      try {
        apple.auth.init({
          clientId: APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: APPLE_REDIRECT_URI,
          // usePopup keeps us off Apple's full-page redirect, which would dump
          // our React state on return. We need that state (signupToken etc.
          // on the sign-up wizard) to survive the round-trip — same rationale
          // as Google's ux_mode='popup'.
          usePopup: true,
        });
        setReady(true);
      } catch (err) {
        // init throws synchronously on misconfiguration (bad client id format,
        // missing redirect URI). Leave ready=false; the button stays disabled.
        console.warn('[useAppleSignIn] AppleID.auth.init failed', err);
      }
    };

    tryInit();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [configured]);

  const signIn = useCallback(async (): Promise<AppleSignInResult> => {
    if (!configured) {
      throw { reason: 'not_configured' } satisfies AppleSignInError;
    }
    const apple = (window as unknown as { AppleID?: AppleIDGlobal }).AppleID;
    if (!apple?.auth || !ready) {
      throw { reason: 'unavailable' } satisfies AppleSignInError;
    }

    // Cancel any in-flight resolver before opening a new popup.
    if (pendingRef.current) {
      pendingRef.current.reject({ reason: 'cancelled' });
      pendingRef.current = null;
    }

    try {
      const response = await apple.auth.signIn();
      const idToken = response?.authorization?.id_token;
      if (!idToken) {
        throw { reason: 'unknown', detail: 'no id_token in Apple response' } satisfies AppleSignInError;
      }
      return {
        idToken,
        name: response.user?.name
          ? {
              firstName: response.user.name.firstName,
              lastName: response.user.name.lastName,
            }
          : undefined,
      };
    } catch (err) {
      // Apple's signIn rejects with a structured object on cancellation:
      //   { error: 'popup_closed_by_user' } | { error: 'user_cancelled_authorize' }
      const errorCode =
        typeof err === 'object' && err !== null && 'error' in err
          ? String((err as { error?: unknown }).error ?? '')
          : '';
      if (
        errorCode === 'popup_closed_by_user' ||
        errorCode === 'user_cancelled_authorize'
      ) {
        throw { reason: 'cancelled' } satisfies AppleSignInError;
      }
      // Re-throw anything we minted ourselves above unmodified.
      if (typeof err === 'object' && err !== null && 'reason' in err) {
        throw err as AppleSignInError;
      }
      throw {
        reason: 'unknown',
        detail: err instanceof Error ? err.message : errorCode || 'apple sign-in failed',
      } satisfies AppleSignInError;
    }
  }, [configured, ready]);

  return useMemo(() => ({ configured, ready, signIn }), [configured, ready, signIn]);
}

// ---------------------------------------------------------------------------
// Minimal types for the bits of Sign in with Apple JS we actually use.
// ---------------------------------------------------------------------------

interface AppleSignInResponse {
  authorization: {
    code: string;
    id_token: string;
    state?: string;
  };
  /** Present only on the FIRST sign-in. Apple never sends it again. */
  user?: {
    name?: {
      firstName?: string;
      lastName?: string;
    };
    email?: string;
  };
}

interface AppleIDAuth {
  init: (config: {
    clientId: string;
    scope: string;
    redirectURI: string;
    usePopup?: boolean;
    state?: string;
    nonce?: string;
  }) => void;
  signIn: () => Promise<AppleSignInResponse>;
}

interface AppleIDGlobal {
  auth: AppleIDAuth;
}
